import { useState, useEffect } from 'react';
import type { LeadsAdvancedFilters } from '../types';
import type { CardDensity, KanbanSortMode } from '@troia-v3/types';
import { isCardDensity, LEAD_KANBAN_SORT_MODES } from '@troia-v3/types';

/**
 * Chaves do localStorage para preferências do CRM
 */
const STORAGE_KEYS = {
  SELECTED_FUNNEL_ID: 'crm_selected_funnel_id',
  SIDEBAR_OPEN: 'crm_sidebar_open',
  ADVANCED_FILTERS: 'crm_advanced_filters',
  SORT_MODE: 'crm_sort_mode',
  CARD_DENSITY: 'crm_card_density'
} as const;

/** Padrão do CRM: card Normal (`02 Coluna de pessoas`) — spec § 1. */
const DEFAULT_CARD_DENSITY: CardDensity = 'normal';

/**
 * Interface de preferências do CRM
 */
export interface CrmPreferences {
  selectedFunnelId: string | null;
  isSidebarOpen: boolean;
  advancedFilters: LeadsAdvancedFilters;
  sortMode: KanbanSortMode;
  cardDensity: CardDensity;
}

/**
 * Hook customizado para gerenciar preferências do CRM com persistência em localStorage
 *
 * Salva e recupera automaticamente:
 * - Funil selecionado
 * - Estado da sidebar (aberta/fechada)
 * - Filtros avançados aplicados
 *
 * @example
 * ```tsx
 * const {
 *   selectedFunnelId,
 *   setSelectedFunnelId,
 *   isSidebarOpen,
 *   setIsSidebarOpen,
 *   advancedFilters,
 *   setAdvancedFilters
 * } = useCrmPreferences();
 * ```
 */
export const useCrmPreferences = () => {
  // Carregar preferências do localStorage na inicialização
  const [selectedFunnelId, setSelectedFunnelIdState] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_FUNNEL_ID);
      return stored || null;
    } catch {
      return null;
    }
  });

  const [isSidebarOpen, setIsSidebarOpenState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SIDEBAR_OPEN);
      // Default: sidebar fechada (false)
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const [advancedFilters, setAdvancedFiltersState] = useState<LeadsAdvancedFilters>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ADVANCED_FILTERS);
      // Default: filtros vazios
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const [sortMode, setSortModeState] = useState<KanbanSortMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SORT_MODE);
      return stored && (LEAD_KANBAN_SORT_MODES as string[]).includes(stored) ? (stored as KanbanSortMode) : 'created';
    } catch {
      return 'created';
    }
  });

  // Densidade do card do kanban — whitelist na LEITURA (mesmo cuidado do sortMode
  // acima): valor legado, de outro módulo ou editado à mão no storage não pode
  // chegar ao board e quebrar a estimativa de altura da coluna virtualizada.
  const [cardDensity, setCardDensityState] = useState<CardDensity>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CARD_DENSITY);
      return isCardDensity(stored) ? stored : DEFAULT_CARD_DENSITY;
    } catch {
      return DEFAULT_CARD_DENSITY;
    }
  });

  // Salvar selectedFunnelId no localStorage sempre que mudar
  useEffect(() => {
    try {
      if (selectedFunnelId) {
        localStorage.setItem(STORAGE_KEYS.SELECTED_FUNNEL_ID, selectedFunnelId);
      } else {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_FUNNEL_ID);
      }
    } catch (error) {
      console.error('Erro ao salvar selectedFunnelId no localStorage:', error);
    }
  }, [selectedFunnelId]);

  // Salvar isSidebarOpen no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, JSON.stringify(isSidebarOpen));
    } catch (error) {
      console.error('Erro ao salvar isSidebarOpen no localStorage:', error);
    }
  }, [isSidebarOpen]);

  // Salvar sortMode no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SORT_MODE, sortMode);
    } catch (error) {
      console.error('Erro ao salvar sortMode no localStorage:', error);
    }
  }, [sortMode]);

  // Salvar cardDensity no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CARD_DENSITY, cardDensity);
    } catch (error) {
      console.error('Erro ao salvar cardDensity no localStorage:', error);
    }
  }, [cardDensity]);

  // Salvar advancedFilters no localStorage sempre que mudar
  // SKIP FIRST SAVE: Evitar salvar {} no primeiro render quando localStorage não existe
  const [skipFirstSaveFilters, setSkipFirstSaveFilters] = useState(true);

  useEffect(() => {
    if (skipFirstSaveFilters) {
      setSkipFirstSaveFilters(false);
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEYS.ADVANCED_FILTERS, JSON.stringify(advancedFilters));
    } catch (error) {
      console.error('Erro ao salvar advancedFilters no localStorage:', error);
    }
  }, [advancedFilters, skipFirstSaveFilters]);

  return {
    // Funil selecionado
    selectedFunnelId,
    setSelectedFunnelId: setSelectedFunnelIdState,

    // Estado da sidebar
    isSidebarOpen,
    setIsSidebarOpen: setIsSidebarOpenState,

    // Filtros avançados
    advancedFilters,
    setAdvancedFilters: setAdvancedFiltersState,

    // Modo de ordenação do kanban
    sortMode,
    setSortMode: setSortModeState,

    // Densidade do card do kanban
    cardDensity,
    setCardDensity: setCardDensityState
  };
};
